import { useState, useEffect } from 'react'
import { DateTime } from 'luxon'
import styled from 'styled-components'
import * as FA from 'react-icons/fa'
import AuthService from 'services/auth'
import cutileiApi from 'services/cutileiApi'
import CashFlow from 'services/CashFlow'
import { isCanceled, getApiErrorMessage, logApiError } from 'services/apiError'
import useAbortController from 'hooks/useAbortController'
import DateInput from 'components/Inputs/DateInput'
import DreExpenseModal from 'pages/Modals/DreExpenseModal'
import { WRAP_STYLE } from './Goals/GoalsHeader'
import { ReactComponent as Loading } from 'icons/loading2.svg'

import {
  ExpensesContainer,
  Section,
  SectionTitle,
  HintText,
  ActionButton,
  CardsRow,
  Card,
  CardLabel,
  CardMoney,
  CardHint,
  TableWrapper,
  Table,
  HeaderCell,
  LabelCell,
  ValueCell,
  IconButton,
  WarningText,
  MoneyText,
  LoadingContainer,
  InfoText
} from 'pages/Financial/Dre/styles'
import CheckBox from 'components/CheckBox'

// Sem teto a tabela esticaria com a resolução; ela é larga de qualquer jeito e rola de lado.
const MAX_CONTENT_WIDTH = 1440
const SIDE_MARGIN = 16
const OVERDUE_COLOR = '#FF3939'

// As colunas da aba "Débito" da planilha, na ordem dela, com a largura de cada uma.
const COLUMNS = [
  { key: 'supplier', label: 'Fornecedor', width: 140 },
  { key: 'documentNumber', label: 'Número', width: 80 },
  { key: 'description', label: 'Descrição', width: 150 },
  { key: 'account', label: 'Conta contábil', width: 130 },
  { key: 'planned', label: 'Conta planejada', width: 100 },
  { key: 'subaccount', label: 'Subconta', width: 130 },
  { key: 'costCenter', label: 'Centro de custo', width: 100 },
  { key: 'entryType', label: 'Tipo', width: 80 },
  { key: 'issueDate', label: 'Emissão', width: 95 },
  { key: 'dueDate', label: 'Vencimento', width: 95 },
  { key: 'paidAt', label: 'Pagamento', width: 95 },
  { key: 'competence', label: 'Mês pgto', width: 80 },
  { key: 'value', label: 'Valor', width: 105 }
]
const ACTIONS_WIDTH = 80

const KIND_LABELS = { fixed: 'Fixo', variable: 'Variável' }

const formatDate = isoDate => (isoDate ? DateTime.fromISO(isoDate).toFormat('dd/MM/yyyy') : '–')
const formatMoney = value => value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
const textOrDash = value => value || '–'

const CardValue = styled.span`
  color: ${({ tone }) => tone ?? '#252525'};
  font-size: 20px;
  font-weight: 600;
`

/*
  A corrida do salão: quanto há de débito em aberto até a data final e quanto entra de recebível
  até lá. O resultado de `CashFlow.debtRace` em quatro cartões.
*/
function RaceCards({ race, endDate }) {
  const until = DateTime.fromISO(endDate).toFormat('dd/MM')
  const negative = race.balance < 0

  return (
    <CardsRow>
      <Card>
        <CardLabel>Débitos em aberto até {until}</CardLabel>
        <CardMoney value={race.debts}/>
        <CardHint>
          {race.overdueCount > 0 ? `${race.overdueCount} vencidas (${formatMoney(race.overdue)}) + ` : ''}
          {race.upcomingCount} a vencer ({formatMoney(race.upcoming)})
        </CardHint>
      </Card>
      <Card>
        <CardLabel>Recebíveis até {until}</CardLabel>
        <CardMoney value={race.income}/>
        <CardHint>O que cai na conta de hoje até lá, líquido de taxa</CardHint>
      </Card>
      <Card>
        <CardLabel>Saldo</CardLabel>
        <CardMoney value={race.balance} tone={negative ? OVERDUE_COLOR : '#44CF6C'}/>
        <CardHint>{negative ? 'Faltam recebíveis para cobrir os débitos' : 'Os recebíveis cobrem os débitos'}</CardHint>
      </Card>
      <Card>
        <CardLabel>Cobertura</CardLabel>
        <CardValue tone={negative ? OVERDUE_COLOR : '#44CF6C'}>
          {race.coverage === null ? '–' : `${(race.coverage * 100).toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%`}
        </CardValue>
        <CardHint>Parte dos débitos que a receita paga</CardHint>
      </Card>
    </CardsRow>
  )
}

export default function Payables({ year, month }) {
  const businessId = AuthService.getBusinessId()
  const requestAuth = AuthService.getRequestAuth()
  const canEdit = AuthService.hasPermission('financial.dre', 'edit')
  const { abortarPendentes } = useAbortController()

  const [loading, setLoading] = useState(true)
  const [errorMessage, setErrorMessage] = useState(null)
  const [categories, setCategories] = useState([])
  const [monthExpenses, setMonthExpenses] = useState([])
  const [openExpenses, setOpenExpenses] = useState([])
  const [receivables, setReceivables] = useState(null)
  const [onlyOpen, setOnlyOpen] = useState(false)
  // A data final escolhida vale só para o mês em que foi escolhida.
  const [chosenEnd, setChosenEnd] = useState(null)
  const [modalExpense, setModalExpense] = useState(null)
  const [modalVisible, setModalVisible] = useState(false)

  const today = DateTime.now().toISODate()
  const monthKey = `${year}-${month}`
  const { endDate: monthEnd } = CashFlow.monthRange(year, month)
  const raceEnd = chosenEnd?.monthKey === monthKey ? chosenEnd.date : monthEnd
  // Mês que já acabou não tem corrida: o que sobra de débito dele aparece na tabela.
  const hasRace = raceEnd >= today

  useEffect(() => {
    getData()
  }, [year, month, raceEnd])

  useEffect(() => {
    loadCategories()
  }, [])

  // `silent` atualiza os dados por baixo sem trocar a tabela pelo carregando.
  const getData = async ({ silent = false } = {}) => {
    const signal = abortarPendentes()

    try {
      if (!silent) setLoading(true)
      setErrorMessage(null)

      const [monthResult, openResult, receivablesResult] = await Promise.all([
        CashFlow.getPayables({ businessId, year, month, requestAuth, signal }),
        hasRace ? CashFlow.getPayables({ businessId, paid: false, dueEnd: raceEnd, requestAuth, signal }) : [],
        hasRace
          ? CashFlow.getReceivables({ businessId, startDate: today, endDate: raceEnd, requestAuth, signal })
          : null
      ])

      setMonthExpenses(monthResult)
      setOpenExpenses(openResult)
      setReceivables(receivablesResult)
    } catch (error) {
      if (isCanceled(error)) return
      logApiError(error)
      setErrorMessage(getApiErrorMessage(error, 'Não foi possível carregar as contas a pagar.'))
    } finally {
      if (!signal.aborted) setLoading(false)
    }
  }

  const loadCategories = async () => {
    try {
      const { data } = await cutileiApi.get(`/businesses/${businessId}/dre_expense_categories`, requestAuth)
      setCategories(data)
    } catch (error) {
      logApiError(error)
      setErrorMessage(getApiErrorMessage(error))
    }
  }

  // A linha muda na hora e o servidor confirma depois; se falhar, a releitura desfaz a mudança.
  // A releitura também atualiza a corrida (débitos em aberto), sem trocar a tabela pelo carregando.
  const togglePaid = async (expense, paid) => {
    const patch = list => list.map(item => (
      item.id === expense.id ? { ...item, paid, paid_at: paid ? today : null } : item
    ))

    try {
      setErrorMessage(null)
      setMonthExpenses(patch)
      setOpenExpenses(list => (paid ? list.filter(item => item.id !== expense.id) : list))
      await CashFlow.markPaid({ businessId, expenseId: expense.id, paid, requestAuth })
    } catch (error) {
      logApiError(error)
      setErrorMessage(getApiErrorMessage(error))
    }

    getData({ silent: true })
  }

  const openModal = expense => {
    setModalExpense(expense)
    setModalVisible(true)
  }

  // Os débitos do mês (por competência) mais os em aberto até a data final, que podem ser de outros meses.
  const rows = [...new Map([...monthExpenses, ...openExpenses].map(expense => [expense.id, expense])).values()]
    .map(expense => ({ ...expense, effective_due_date: CashFlow.effectiveDueDate(expense) }))
    .filter(expense => !onlyOpen || !expense.paid)
    .sort((a, b) => a.effective_due_date.localeCompare(b.effective_due_date))

  const race = hasRace ? CashFlow.debtRace({ openExpenses, receivables, today, endDate: raceEnd }) : null

  const cellOf = (expense, key) => {
    const overdue = !expense.paid && expense.effective_due_date < today

    switch (key) {
      case 'supplier': return textOrDash(expense.supplier)
      case 'documentNumber': return textOrDash(expense.document_number)
      case 'description': return textOrDash(expense.description)
      case 'account': return expense.category?.name ?? '–'
      case 'planned': return KIND_LABELS[expense.category?.kind] ?? '–'
      case 'subaccount': return textOrDash(expense.subaccount)
      case 'costCenter': return textOrDash(expense.cost_center)
      case 'entryType': return textOrDash(expense.entry_type)
      case 'issueDate': return formatDate(expense.issue_date)
      case 'dueDate':
        return (
          <span style={overdue ? { color: OVERDUE_COLOR, fontWeight: 600 } : undefined}>
            {formatDate(expense.effective_due_date)}
            {expense.due_date ? '' : ' *'}
          </span>
        )
      case 'paidAt': return formatDate(expense.paid_at)
      case 'competence': return DateTime.fromISO(expense.competence_month).toFormat('MM/yyyy')
      default: return null
    }
  }

  return (
    <ExpensesContainer style={{ maxWidth: MAX_CONTENT_WIDTH, width: `calc(100% - ${SIDE_MARGIN * 2}px)` }}>
      <Section>
        <SectionTitle>Corrida dos débitos</SectionTitle>
        {hasRace ? (
          <>
            <HintText>
              Débitos em aberto (vencidos e a vencer) contra o que cai na conta de hoje até a data final.
              Não conta vendas que ainda vão acontecer.
            </HintText>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <HintText>Até</HintText>
              <div style={{ width: 200 }}>
                <DateInput
                  key={monthKey}
                  value={raceEnd}
                  disabledDates={({ date }) => DateTime.fromJSDate(date) < DateTime.now().startOf('day')}
                  onChange={date => setChosenEnd({ monthKey, date })}
                />
              </div>
            </div>
            {!loading && race && <RaceCards race={race} endDate={raceEnd}/>}
          </>
        ) : (
          <HintText>Este mês já acabou: a corrida só vale de hoje em diante. Os débitos dele estão na tabela.</HintText>
        )}
      </Section>
      <Section>
        <SectionTitle>Débitos</SectionTitle>
        <HintText>
          Os débitos do mês e os em aberto até a data final. Vencimento com * é o fim do mês de competência
          (a despesa não tem vencimento). Elas são as mesmas da aba Despesas da DRE.
        </HintText>
        {canEdit && (
          <ActionButton type='button' onClick={() => openModal(null)}>+ Nova despesa</ActionButton>
        )}
        <label>
          <CheckBox value={onlyOpen} onValueChange={setOnlyOpen}/>
          {' '}Só em aberto
        </label>
        {loading ? (
          <LoadingContainer>
            <Loading/>
            <InfoText>Carregando contas a pagar...</InfoText>
          </LoadingContainer>
        ) : rows.length === 0 ? (
          <HintText>{onlyOpen ? 'Nenhuma conta a pagar em aberto.' : 'Nenhum débito neste mês.'}</HintText>
        ) : (
          <TableWrapper framed>
            <Table style={{ minWidth: COLUMNS.reduce((sum, column) => sum + column.width, canEdit ? ACTIONS_WIDTH : 0) }}>
              <colgroup>
                {COLUMNS.map(column => <col key={column.key} style={{ width: column.width }}/>)}
                {canEdit && <col style={{ width: ACTIONS_WIDTH }}/>}
              </colgroup>
              <thead>
                <tr>
                  {COLUMNS.map((column, index) => (
                    <HeaderCell key={column.key} sticky={index === 0} align={index === 0 ? 'left' : undefined} style={WRAP_STYLE}>
                      {column.label}
                    </HeaderCell>
                  ))}
                  {canEdit && <HeaderCell>Ações</HeaderCell>}
                </tr>
              </thead>
              <tbody>
                {rows.map(expense => (
                  <tr key={expense.id}>
                    {COLUMNS.map((column, index) => {
                      if (column.key === 'value') {
                        return (
                          <ValueCell key={column.key}>
                            <MoneyText kind='total' value={Number(expense.value)}/>
                          </ValueCell>
                        )
                      }

                      return index === 0 ? (
                        <LabelCell key={column.key} style={WRAP_STYLE}>{cellOf(expense, column.key)}</LabelCell>
                      ) : (
                        <ValueCell key={column.key} style={{ ...WRAP_STYLE, textAlign: 'left' }}>
                          {cellOf(expense, column.key)}
                        </ValueCell>
                      )
                    })}
                    {canEdit && (
                      <ValueCell style={{ textAlign: 'center' }}>
                        <IconButton
                          type='button'
                          aria-label={expense.paid ? 'Desfazer pagamento' : 'Marcar como paga'}
                          title={expense.paid ? 'Paga — clique para desfazer' : 'Não paga — clique para marcar como paga'}
                          onClick={() => togglePaid(expense, !expense.paid)}
                        >
                          {expense.paid
                            ? <FA.FaCheck color='#44CF6C' size={14}/>
                            : <FA.FaTimes color={OVERDUE_COLOR} size={14}/>}
                        </IconButton>
                        <IconButton type='button' aria-label='Editar despesa' onClick={() => openModal(expense)}>
                          <FA.FaPen color='#252525' size={14}/>
                        </IconButton>
                      </ValueCell>
                    )}
                  </tr>
                ))}
                <tr>
                  <LabelCell kind='total'>Total ({rows.length})</LabelCell>
                  <ValueCell kind='total' colSpan={COLUMNS.length - 2}/>
                  <ValueCell kind='total'>
                    <MoneyText kind='total' value={CashFlow.sumValues(rows)}/>
                  </ValueCell>
                  {canEdit && <ValueCell kind='total'/>}
                </tr>
              </tbody>
            </Table>
          </TableWrapper>
        )}
      </Section>
      {errorMessage && <WarningText>{errorMessage}</WarningText>}
      <DreExpenseModal
        visible={modalVisible}
        year={modalExpense ? Number(modalExpense.competence_month.slice(0, 4)) : year}
        expense={modalExpense}
        categories={categories}
        onCategoryCreated={category => setCategories([...categories, category])}
        onConfirm={() => getData({ silent: true })}
        onClose={() => setModalVisible(false)}
      />
    </ExpensesContainer>
  )
}
